/**
 * Social OAuth connection APIs (Instagram, YouTube, Facebook, TikTok).
 * Backend routes live under /api/social/:platform/*.
 */

import { getApiBaseUrl, getBaseHeaders } from "./api";

let _emojiCache = null;

/**
 * GET /api/social/emojis — fetches categorized emoji list. Cached in-memory
 * after the first call so repeated picker opens don't hit the network.
 */
export async function fetchEmojis() {
    if (_emojiCache) return { ok: true, data: _emojiCache };
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/emojis`);
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data?.categories) {
        _emojiCache = json.data;
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || 'Failed to load emojis' };
}

/**
 * GET /api/social/giphy/search?q=...&limit=&offset= — proxy search through
 * our backend so the Giphy API key stays server-side.
 */
export async function searchGiphy({ q, limit = 24, offset = 0, accessToken } = {}) {
    const base = getApiBaseUrl();
    const query = (q ?? '').trim();
    if (!query) return { ok: true, data: { found: false, results: [], total: 0 } };
    const params = new URLSearchParams({ q: query, limit: String(limit), offset: String(offset) });
    const res = await fetch(`${base}/api/social/giphy/search?${params.toString()}`, {
        method: 'GET',
        headers: { ...getBaseHeaders(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/** GET /api/social/giphy/trending?limit= — feed for the empty-query state. */
export async function getGiphyTrending({ limit = 24, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/giphy/trending?limit=${limit}`, {
        method: 'GET',
        headers: { ...getBaseHeaders(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * GET /api/social/instagram/inbox — list DM conversations powered by
 * instagram_dm_logs (auto-DMs we sent in response to comments) +
 * instagram_messages (manual outbound and inbound chat). Each conversation
 * matches the SocialInboxView shape:
 *   { id, type: 'dm', handle, avatarEmoji, threadUserId, unread, lastAt, messages: [...] }
 */
export async function fetchInstagramInbox({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/instagram/inbox`, {
        method: 'GET',
        headers: { ...getBaseHeaders(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/social/instagram/inbox/mark-read — clear the unread badge on a
 * thread by marking every inbound row read. Idempotent.
 */
export async function markInstagramThreadRead({ threadUserId, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/instagram/inbox/mark-read`, {
        method: 'POST',
        headers: {
            ...getBaseHeaders(),
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ threadUserId }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) return { ok: true, data: json.data };
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/social/instagram/inbox/messages — send a manual DM from the
 * inbox composer. The recipient must have messaged the merchant within the
 * last 24 hours (Instagram Standard Messaging Window) — outside that window
 * Instagram returns code 10 / subcode 2534022. The backend persists the
 * attempt either way (status='sent' or 'failed') so the failure shows in
 * the thread for the merchant to see.
 */
export async function sendInstagramInboxMessage({ threadUserId, text, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/instagram/inbox/messages`, {
        method: 'POST',
        headers: {
            ...getBaseHeaders(),
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ threadUserId, text }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data?.message) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * GET /api/social/instagram/search-user?q=xxx — search connected Instagram
 * users from our DB by handle or display name.
 */
export async function searchInstagramUser({ q, accessToken } = {}) {
    const base = getApiBaseUrl();
    const query = encodeURIComponent((q ?? '').trim().replace(/^@/, ''));
    if (!query) return { ok: true, data: { users: [] } };
    const res = await fetch(`${base}/api/social/instagram/search-user?q=${query}`, {
        method: 'GET',
        headers: { ...getBaseHeaders(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return headers;
}

/**
 * GET /api/social — list connected platforms (no tokens, no live data).
 * @param {{ accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { connections: Array<{ platform: string, handle: string|null, profileUrl: string|null, connectedAt: string, expiresAt: string|null }> }, error?: string }>}
 */
export async function listSocialConnections({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * GET /api/social/:platform — fetch live profile snapshot for a connected platform.
 * Returns followers, following, posts count, recent media, etc.
 * @param {{ platform: string, accessToken?: string }} params
 */
export async function fetchSocialProfile({ platform, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/${encodeURIComponent(platform)}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * GET /api/social/:platform/connect?redirectUrl=... — start OAuth and get the
 * provider consent URL. The backend saves {state, userId, platform, redirectUrl}
 * in the DB; after the user consents, the provider redirects to our callback
 * which then redirects the browser to `redirectUrl` with `?code=&state=`.
 *
 * @param {{ platform: string, redirectUrl?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { platform: string, redirectUrl: string }, error?: string }>}
 */
export async function startSocialConnect({ platform, redirectUrl, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : "";
    const res = await fetch(`${base}/api/social/${encodeURIComponent(platform)}/connect${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/social/:platform/verify — exchange the authorization code for tokens
 * and persist the connection. Called by the frontend after it lands on the
 * redirectUrl with `?code=&state=` set by the backend callback redirect.
 *
 * For WhatsApp, if the discovered number is already connected to a different
 * account, the backend responds with `{ conflict: true, phoneNumber, displayName }`
 * instead of persisting. Resubmitting with `force: true` (same `state`, `code`
 * can be omitted) confirms the takeover.
 *
 * @param {{ platform: string, code?: string, state: string, force?: boolean, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { platform: string, handle: string|null, profileUrl: string|null } | { conflict: true, phoneNumber: string|null, displayName: string|null }, error?: string }>}
 */
export async function verifySocialConnect({ platform, code, state, force, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/${encodeURIComponent(platform)}/verify`, {
        method: "POST",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ code, state, force }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * PATCH /api/social/:platform/preferences — update per-connection prefs
 * (timezone, auto-publish). Only fields provided in the body are changed.
 *
 * @param {{ platform: string, timezone?: string|null, autoPublish?: boolean, accessToken?: string }} params
 */
export async function updateSocialPreferences({ platform, timezone, autoPublish, accessToken } = {}) {
    const base = getApiBaseUrl();
    const body = {};
    if (timezone !== undefined) body.timezone = timezone;
    if (autoPublish !== undefined) body.autoPublish = autoPublish;
    const res = await fetch(`${base}/api/social/${encodeURIComponent(platform)}/preferences`, {
        method: "PATCH",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * DELETE /api/social/:platform — disconnect a connected platform.
 * @param {{ platform: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { disconnected: boolean, platform: string }, error?: string }>}
 */
export async function disconnectSocial({ platform, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social/${encodeURIComponent(platform)}`, {
        method: "DELETE",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/* ── Social Posts CRUD ── */

/**
 * POST /api/social-posts — create a new post (draft / scheduled / publish-now).
 * @param {{ platform: string, status: string, caption?: string, media?: Array, scheduledAt?: string, firstComment?: string, linkInBio?: string, location?: string, collaborators?: Array, postTags?: string[], campaignTags?: string[], accessToken?: string }} params
 */
export async function createSocialPost({ accessToken, ...body } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social-posts`, {
        method: "POST",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * GET /api/social-posts — list posts with optional filters.
 * @param {{ platform?: string, status?: string, from?: string, to?: string, accessToken?: string }} params
 */
export async function listSocialPosts({ accessToken, platform, status, from, to } = {}) {
    const base = getApiBaseUrl();
    const params = new URLSearchParams();
    if (platform) params.set("platform", platform);
    if (status) params.set("status", status);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const res = await fetch(`${base}/api/social-posts${qs}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * GET /api/social-posts/:id — get a single post.
 */
export async function getSocialPost({ id, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social-posts/${id}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * PUT /api/social-posts/:id — update a draft/scheduled post.
 */
export async function updateSocialPost({ id, accessToken, ...body } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social-posts/${id}`, {
        method: "PUT",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * DELETE /api/social-posts/:id — delete a draft/scheduled/failed post.
 */
export async function deleteSocialPost({ id, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/social-posts/${id}`, {
        method: "DELETE",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}
